'use client';

import React, { useEffect, useState, type CSSProperties } from 'react';

import { PrismLight as PrismSyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

import twMerge from 'utilities/src/twMerge';

import IconComponent from '../Icon';

/**
 * Types
 */

export type SyntaxHighlighterProps = {
  code: string;
  language: { code: string; name: string };
  description?: string;
  className?: string;
  customStyle?: CSSProperties;
};

/**
 * Component
 */

const registeredLanguages = new Set<string>();

export default function SyntaxHighlighter({
  code,
  language,
  description,
  className,
  customStyle = {},
}: SyntaxHighlighterProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [, setIsLanguageLoaded] = useState(false);

  /**
   * Register language
   */

  useEffect(() => {
    if (!language.code || registeredLanguages.has(language.code)) return;
    if (language.code === 'html') return;

    const loadLanguage = async () => {
      try {
        const langModule = await import(`react-syntax-highlighter/dist/esm/languages/prism/${language.code}`);

        PrismSyntaxHighlighter.registerLanguage(language.code, langModule.default);
        registeredLanguages.add(language.code);

        setIsLanguageLoaded(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Failed to load language: ${language.name} with code ${language.code}`, error);
      }
    };

    loadLanguage();
  }, [language]);

  /**
   * Handle copy
   */

  const handleCopy = () => {
    navigator.clipboard.writeText(code);

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  /**
   * Render
   */

  return (
    <div className={twMerge('flex max-h-[21.25rem] flex-col', className)}>
      <div className="border-b-site-neutrals-600 bg-site-neutrals-900 flex items-center justify-between rounded-t-lg border-b px-4 py-1">
        <div className="flex items-center gap-2">
          <span className="site-display-xs text-site-neutrals-0 font-bold">{language.name}</span>

          {description && <span className="site-paragraph-xl text-site-neutrals-200">{description}</span>}
        </div>

        <button type="button" onClick={handleCopy}>
          {isCopied ? (
            <IconComponent iconName="Check" size="IconL" className="bg-site-secondary-green-400" />
          ) : (
            <IconComponent iconName="ContentCopy" size="IconSM" className="bg-site-neutrals-0" />
          )}
        </button>
      </div>

      <PrismSyntaxHighlighter
        language={language.code}
        style={materialDark}
        customStyle={{
          margin: 0,
          borderBottomRightRadius: 8,
          borderBottomLeftRadius: 8,
          ...customStyle,
        }}
        showLineNumbers
      >
        {code}
      </PrismSyntaxHighlighter>
    </div>
  );
}
